import {
  Activity,
  AlertCircle,
  Baby,
  BookOpen,
  Boxes,
  Braces,
  Bug,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Component,
  Database,
  ExternalLink,
  FileSearch,
  FileStack,
  FileText,
  FlaskConical,
  GitBranch,
  GitCompare,
  Layers,
  ListFilter,
  Pill,
  ReceiptText,
  Route,
  ScanLine,
  Moon,
  Search,
  ShieldCheck,
  Stethoscope,
  Sun,
  Syringe,
  Table2
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  loadCatalog,
  loadCollection,
  loadManifest,
  loadReference,
  loadSearchIndex
} from "./data";
import articlesCatalog from "./content/articles.json";
import { stripAppBasePath, withAppBasePath } from "./routing";
import type {
  Catalog,
  CollectionItem,
  CollectionSummary,
  ComponentRow,
  FieldRow,
  Manifest,
  NarrativeBlock,
  ReferenceDetail,
  ReferenceKind,
  ReferenceLink,
  SearchHit,
  SourceResource,
  StructureNode,
  TableValue,
  VersionSummary
} from "./types";

const kindLabels: Record<ReferenceKind, string> = {
  message: "Messages",
  segment: "Segments",
  field: "Fields",
  datatype: "Data Types",
  table: "Tables"
};

const kindSingularLabels: Record<ReferenceKind, string> = {
  message: "message",
  segment: "segment",
  field: "field",
  datatype: "datatype",
  table: "table"
};

const kindIcons: Record<ReferenceKind, typeof BookOpen> = {
  message: GitBranch,
  segment: Layers,
  field: Component,
  datatype: Boxes,
  table: Table2
};

const kindOrder: ReferenceKind[] = ["message", "segment", "datatype", "table"];
const hl7SoupWebUrl = "https://hl7soup.integrationsoup.com";
const homeArticlePath = "/content/articles/home.html";
const versionGuidePath = "/articles/which-hl7-version-should-i-use";
const homeArticleDescription =
  "A practical HL7 reference guide for learning HL7 v2, reading messages, using HL7 Soup Web, and building Integration Soup workflows that connect healthcare systems.";

function buildHl7SoupReferenceTooltip(reference: string) {
  const nextReference = reference.replace(/}$/, ">}");
  const randomReference = reference.replace(/}$/, "?}");
  return `Use ${reference} in HL7 Soup workflows to pull values from the built-in HL7 table. Use ${nextReference} to move to the next row before reading, or ${randomReference} to pick a random row. Generic data tables use the separate ${"${DataTable:path.field}"} syntax for lookup or anonymized data.`;
}

type SiteSection =
  | "home"
  | "articles"
  | "segments"
  | "messages"
  | "datatypes"
  | "tables"
  | "fields";

type AppPage = "home" | "article-index" | "article" | "reference";

interface ArticleRecord {
  id: string;
  title: string;
  description: string;
  category: "articles" | "segments" | "messages" | "datatypes" | "tables";
  route: string;
  sourcePath: string;
  messageCode?: string;
  structureCode?: string;
  segmentCode?: string;
  datatypeCode?: string;
  fieldsPanelDefaultOpen?: boolean;
  structurePanelDefaultOpen?: boolean;
}

interface ArticleCatalog {
  schemaVersion: string;
  defaultVersion: string;
  baseUrl: string;
  articles: ArticleRecord[];
}

interface AppRoute {
  pathname: string;
  hash: string;
  section: SiteSection;
  page: AppPage;
  article?: ArticleRecord;
  referenceKind?: ReferenceKind;
  code?: string;
}

interface BrowserResult {
  id: string;
  path: string;
  code: string;
  title: string;
  summary: string;
  kind?: ReferenceKind;
}

function getArticleBrowserResult(article: ArticleRecord): BrowserResult {
  return {
    id: `article-${article.id}`,
    path: article.route,
    code: "Article",
    title: article.title,
    summary: article.description
  };
}

function browserResultMatchesQuery(item: BrowserResult, normalizedQuery: string) {
  if (!normalizedQuery) return true;
  return `${item.code} ${item.title} ${item.summary}`.toLowerCase().includes(normalizedQuery);
}

const articleData = articlesCatalog as ArticleCatalog;
const integrationSoupHomeUrl = "https://www.integrationsoup.com";
const defaultVersionId = articleData.defaultVersion || "2.5.1";
const articles = articleData.articles;
const articlesByRoute = new Map(articles.map((article) => [article.route, article]));
const segmentArticlesByCode = new Map(
  articles
    .filter((article) => article.segmentCode)
    .map((article) => [article.segmentCode!, article])
);
const messageArticlesByCode = new Map(
  articles
    .filter((article) => article.messageCode)
    .map((article) => [article.messageCode!.toUpperCase(), article])
);
const datatypeArticlesByCode = new Map(
  articles
    .filter((article) => article.datatypeCode)
    .map((article) => [article.datatypeCode!, article])
);
const articleList = articles.filter((article) => article.category === "articles");
const segmentGuideArticle = articlesByRoute.get("/segments");
const messageGuideArticle = articlesByRoute.get("/messages");
const tableGuideArticle = articlesByRoute.get("/tables");
const datatypeGuideArticle = articlesByRoute.get("/datatypes");

type ArticleIconKey =
  | "article"
  | "scheduling"
  | "version"
  | "patient"
  | "clinical"
  | "imaging"
  | "immunization"
  | "publicHealth"
  | "finance"
  | "pharmacy"
  | "documents"
  | "masterData"
  | "routing"
  | "debugging"
  | "maternity";

const articleIcons: Record<ArticleIconKey, typeof BookOpen> = {
  article: FileText,
  scheduling: CalendarDays,
  version: GitCompare,
  patient: Stethoscope,
  clinical: FlaskConical,
  imaging: ScanLine,
  immunization: Syringe,
  publicHealth: ShieldCheck,
  finance: ReceiptText,
  pharmacy: Pill,
  documents: FileStack,
  masterData: Database,
  routing: Route,
  debugging: Bug,
  maternity: Baby
};

function getArticleIconKey(article: ArticleRecord): ArticleIconKey {
  const text = `${article.title} ${article.route}`.toLowerCase();

  if (text.includes("debug")) return "debugging";
  if (text.includes("schedule") || text.includes("scheduling")) return "scheduling";
  if (text.includes("version")) return "version";
  if (text.includes("maternity") || text.includes("newborn") || text.includes("mother")) {
    return "maternity";
  }
  if (text.includes("immunization") || text.includes("vxu")) return "immunization";
  if (text.includes("public health")) return "publicHealth";
  if (text.includes("billing") || text.includes("dft")) return "finance";
  if (text.includes("pharmacy")) return "pharmacy";
  if (text.includes("document") || text.includes("pdf")) return "documents";
  if (
    text.includes("radiology") ||
    text.includes("ultrasound") ||
    text.includes("imaging") ||
    text.includes("narrative")
  ) {
    return "imaging";
  }
  if (text.includes("lab") || text.includes("pathology") || text.includes("cancer")) {
    return "clinical";
  }
  if (
    text.includes("master") ||
    text.includes("mfn") ||
    text.includes("provider") ||
    text.includes("identifier")
  ) {
    return "masterData";
  }
  if (text.includes("interface") || text.includes("routing") || text.includes("multi-site")) {
    return "routing";
  }
  if (text.includes("adt") || text.includes("patient") || text.includes("emergency")) {
    return "patient";
  }

  return "article";
}

const navItems: Array<{ section: SiteSection; label: string; path: string }> = [
  { section: "home", label: "Home", path: "/" },
  { section: "articles", label: "Articles", path: "/articles" },
  { section: "segments", label: "Segments", path: "/segments" },
  { section: "messages", label: "Messages", path: "/messages" },
  { section: "datatypes", label: "Datatypes", path: "/datatypes" },
  { section: "tables", label: "Tables", path: "/tables" }
];

const kindRouteSegments: Record<ReferenceKind, string> = {
  message: "messages",
  segment: "segments",
  field: "fields",
  datatype: "datatypes",
  table: "tables"
};
const reservedCodeRouteSlugs: Record<string, string> = {
  CON: "con-segment",
  NUL: "nul-datatype"
};
const routeSlugCodeAliases: Record<string, string> = {
  ...Object.fromEntries(Object.entries(reservedCodeRouteSlugs).map(([code, slug]) => [slug, code])),
  var: "var",
  varies: "varies"
};
const fieldsPanelStorageKey = "reference-guide-fields-panel-open";
const componentsPanelStorageKey = "reference-guide-components-panel-open";
const structurePanelStorageKey = "reference-guide-structure-panel-open";

function getHashPath() {
  if (!window.location.hash.startsWith("#item=")) return "";
  const raw = window.location.hash.replace(/^#item=/, "");
  return raw ? decodeURIComponent(raw) : "";
}

function getVersionFromPath(path: string) {
  const match = path.match(/\/data\/hl7\/v([^/]+)\//);
  return match?.[1] ?? "";
}

function getVersionedPath(path: string, versionId: string) {
  if (!path) return "";
  return path.replace(/\/data\/hl7\/v[^/]+\//, `/data/hl7/v${versionId}/`);
}

function getKindFromPath(path: string): ReferenceKind | null {
  const match = path.match(/\/(messages|segments|fields|datatypes|tables)\//);
  const collection = match?.[1];
  if (collection === "messages") return "message";
  if (collection === "segments") return "segment";
  if (collection === "fields") return "field";
  if (collection === "datatypes") return "datatype";
  if (collection === "tables") return "table";
  return null;
}

function getCodeFromReferencePath(path: string) {
  const fileName = path.split("/").pop() ?? "";
  return decodeURIComponent(fileName.replace(/\.json$/i, ""));
}

function getReferenceKindFromRouteSection(section: SiteSection): ReferenceKind | null {
  if (section === "messages") return "message";
  if (section === "segments") return "segment";
  if (section === "datatypes") return "datatype";
  if (section === "tables") return "table";
  if (section === "fields") return "field";
  return null;
}

function codeToRouteSlug(code: string) {
  const reservedSlug = reservedCodeRouteSlugs[code.toUpperCase()];
  if (reservedSlug) return reservedSlug;
  return code.toLowerCase();
}

function routeSlugToCode(slug: string) {
  const decodedSlug = decodeURIComponent(slug).toLowerCase();
  return routeSlugCodeAliases[decodedSlug] ?? decodedSlug.toUpperCase();
}

function getFieldAnchorId(code: string) {
  return code.replace(/\./g, "-").toUpperCase();
}

function getSegmentCodeFromFieldCode(code: string) {
  return code.split(".")[0]?.toUpperCase() ?? "";
}

function getRouteForFieldCode(code: string) {
  const segmentCode = getSegmentCodeFromFieldCode(code);
  if (!segmentCode) return "";
  return `/segments/${codeToRouteSlug(segmentCode)}#${getFieldAnchorId(code)}`;
}

function getFieldReferenceAnchorId(code: string) {
  return `field-reference-${getFieldAnchorId(code).toLowerCase()}`;
}

function getComponentReferenceAnchorId(code: string) {
  return `component-reference-${getFieldAnchorId(code).toLowerCase()}`;
}

function scrollToHashTarget(hash: string) {
  if (!hash || hash === "#") return;

  window.requestAnimationFrame(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    const target = document.getElementById(id);
    target?.scrollIntoView({ block: "start" });
  });
}

function scrollToPageTop() {
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0 });
    document.querySelector(".reference-detail")?.scrollTo({ top: 0, left: 0 });
    document.querySelector(".detail-panel")?.scrollTo({ top: 0, left: 0 });
  });
}

function getRouteForKindCode(kind: ReferenceKind, code: string) {
  if (kind === "field") return getRouteForFieldCode(code);
  const slug = codeToRouteSlug(code);
  return `/${kindRouteSegments[kind]}/${slug}`;
}

function getRouteForReferencePath(path: string) {
  const kind = getKindFromPath(path);
  if (!kind) return "";
  return getRouteForKindCode(kind, getCodeFromReferencePath(path));
}

function compactSearchText(value: string) {
  return value.toLowerCase().replace(/[\s._-]+/g, "");
}

function getSearchHitRank(item: SearchHit, normalizedQuery: string) {
  if (!normalizedQuery) return 0;

  const code = item.code.toLowerCase();
  const title = item.title.toLowerCase();
  const summary = item.summary.toLowerCase();
  const tags = item.tags?.map((tag) => tag.toLowerCase()) ?? [];
  const compactQuery = compactSearchText(normalizedQuery);
  const compactCode = compactSearchText(item.code);

  if (code === normalizedQuery || compactCode === compactQuery) return 0;
  if (code.startsWith(normalizedQuery) || compactCode.startsWith(compactQuery)) return 1;
  if (tags.some((tag) => tag === normalizedQuery)) return 2;
  if (title.startsWith(normalizedQuery)) return 3;
  if (code.includes(normalizedQuery)) return 4;
  if (title.includes(normalizedQuery)) return 5;
  if (summary.includes(normalizedQuery)) return 6;
  return 7;
}

function compareSearchHits(left: SearchHit, right: SearchHit, normalizedQuery: string) {
  const rankDelta = getSearchHitRank(left, normalizedQuery) - getSearchHitRank(right, normalizedQuery);
  if (rankDelta !== 0) return rankDelta;

  const leftKindIndex = kindOrder.indexOf(left.kind);
  const rightKindIndex = kindOrder.indexOf(right.kind);
  const kindDelta =
    (leftKindIndex === -1 ? Number.MAX_SAFE_INTEGER : leftKindIndex) -
    (rightKindIndex === -1 ? Number.MAX_SAFE_INTEGER : rightKindIndex);
  if (kindDelta !== 0) return kindDelta;

  return left.code.localeCompare(right.code) || left.title.localeCompare(right.title);
}

function getReferencePathForRoute(route: AppRoute, versionId?: string) {
  if (!versionId || !route.referenceKind || !route.code) return "";
  return `/data/hl7/v${versionId}/${kindRouteSegments[route.referenceKind]}/${route.code.toLowerCase()}.json`;
}

function splitPathAndHash(path: string) {
  const [pathnamePart, hashPart = ""] = path.split("#");
  return {
    pathname: pathnamePart || "/",
    hash: hashPart ? `#${hashPart}` : ""
  };
}

function normalizePathname(pathname: string) {
  const decodedPathname = decodeURI(pathname || "/");
  if (decodedPathname === "/") return "/";
  return decodedPathname.replace(/\/+$/, "");
}

function getCleanPathFromLegacyPath(pathname: string) {
  const normalized = normalizePathname(pathname);
  const fileName = normalized.split("/").pop() ?? "";

  const uppercaseSegment = normalized.match(/^\/segments\/([A-Z0-9]{2,4})$/);
  if (uppercaseSegment) {
    return `/segments/${uppercaseSegment[1].toLowerCase()}`;
  }

  if (fileName !== fileName.toLowerCase()) {
    const lowerPath = normalized.toLowerCase();
    if (
      lowerPath.startsWith("/messages/") ||
      lowerPath.startsWith("/datatypes/") ||
      lowerPath.startsWith("/tables/") ||
      lowerPath.startsWith("/fields/")
    ) {
      return lowerPath;
    }
  }

  return normalized;
}

function prefixRootRelativeHtmlUrls(html: string) {
  return html.replace(
    /\b(?<attribute>href|src)="(?<path>\/(?:articles|segments|messages|datatypes|tables|fields|data|assets|branding)[^"]*)"/g,
    (_match, attribute: string, path: string) => `${attribute}="${withAppBasePath(path)}"`
  );
}

function escapeHtml(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(value: unknown) {
  return escapeHtml(value).replace(/'/g, "&#39;");
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripHtml(value: string) {
  return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function getFieldIdsCoveredByHeading(id: string, headingHtml: string) {
  const idMatch = id.match(/^([A-Z0-9]{2,4})-(\d+)$/i);
  if (!idMatch) return [];

  const segmentCode = idMatch[1].toUpperCase();
  const numbers = new Set([Number(idMatch[2])]);
  const headingText = stripHtml(headingHtml);
  const rangePattern = new RegExp(`\\b${segmentCode}-(\\d+)\\b\\s+(?:to|through|thru)\\s+\\b${segmentCode}-(\\d+)\\b`, "gi");
  const fieldPattern = new RegExp(`\\b${segmentCode}-(\\d+)\\b`, "gi");

  for (const match of headingText.matchAll(rangePattern)) {
    const start = Number(match[1]);
    const end = Number(match[2]);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

    const low = Math.min(start, end);
    const high = Math.max(start, end);
    for (let number = low; number <= high; number += 1) {
      numbers.add(number);
    }
  }

  for (const match of headingText.matchAll(fieldPattern)) {
    numbers.add(Number(match[1]));
  }

  return [...numbers]
    .filter((number) => Number.isFinite(number))
    .sort((a, b) => a - b)
    .map((number) => `${segmentCode}-${number}`);
}

function buildArticleFieldQuickMetaHtml(
  rows: FieldRow[],
  dataTypeLinks: Map<string, CollectionItem>,
  withBasePath: (path: string) => string,
  options: { headingHtml?: string; headingId?: string } = {}
) {
  const isGrouped = rows.length > 1;
  const items = rows
    .map((row, index) => {
      const fieldAnchor = getFieldAnchorId(row.code);
      const dataType = row.dataType ? dataTypeLinks.get(row.dataType) : null;
      const dataTypeTitle = dataType ? `${row.dataType}: ${dataType.title}` : row.dataType;
      const requiredShortLabel = row.usage?.toUpperCase() ?? "";
      const requiredTitle = row.usage ? getRequiredTitle(row.usage) : "";
      const repeatableIsYes = getRepeatableValue(row.cardinality) === "Yes";
      const repeatableShortLabel = repeatableIsYes ? "R" : "S";
      const repeatableLongLabel = repeatableIsYes ? "Repeatable" : "Single";
      const repeatableTitle = row.cardinality ? getRepeatableTitle(row.cardinality) : repeatableLongLabel;
      const rowTag = !isGrouped && index === 0 ? "h3" : "div";
      const rowId = !isGrouped && index === 0 && options.headingId ? ` id="${escapeAttr(options.headingId)}"` : "";
      const rowClass = `field-quick-meta-row${!isGrouped ? " field-quick-meta-row--heading" : ""}`;

      return `<${rowTag} class="${rowClass}"${rowId}>
        <a class="field-quick-meta-code" href="#${escapeAttr(getFieldReferenceAnchorId(row.code))}" title="${escapeAttr(`Show ${fieldAnchor} in the field reference`)}">${escapeHtml(fieldAnchor)}</a>
        <span class="field-quick-meta-name">${escapeHtml(row.name)}</span>
        ${requiredShortLabel ? `<span class="field-quick-chip field-quick-chip--usage" title="${escapeAttr(requiredTitle)}"><span class="field-quick-label-long">${escapeHtml(requiredTitle)}</span><span class="field-quick-label-short">${escapeHtml(requiredShortLabel)}</span></span>` : ""}
        <span class="field-quick-chip field-quick-chip--repeat" title="${escapeAttr(repeatableTitle)}"><span class="field-quick-label-long">${escapeHtml(repeatableLongLabel)}</span><span class="field-quick-label-short">${escapeHtml(repeatableShortLabel)}</span></span>
        ${
          row.dataType
            ? `<a class="field-quick-chip field-quick-chip--type" href="${escapeAttr(withBasePath(`/datatypes/${codeToRouteSlug(row.dataType)}`))}" title="${escapeAttr(dataTypeTitle)}"><span class="field-quick-chip-prefix">Type</span>${escapeHtml(row.dataType)}</a>`
            : ""
        }
        ${
          row.table
            ? `<a class="field-quick-chip field-quick-chip--table" href="${escapeAttr(withBasePath(`/tables/${row.table.toLowerCase()}`))}" title="${escapeAttr(`HL7 table ${row.table}`)}"><span class="field-quick-chip-prefix">Table</span>${escapeHtml(row.table)}</a>`
            : ""
        }
      </${rowTag}>`;
    })
    .join("");

  if (isGrouped) {
    return `<section class="field-quick-meta field-quick-meta--group" aria-label="Quick field metadata">
      ${options.headingId && options.headingHtml ? `<h3 id="${escapeAttr(options.headingId)}" class="field-quick-meta-heading">${options.headingHtml}</h3>` : ""}
      ${items}
    </section>`;
  }

  return `<div class="field-quick-meta field-quick-meta--single" aria-label="Quick field metadata">${items}</div>`;
}

function addSegmentFieldMetadataToArticleHtml(
  html: string,
  detail: Extract<ReferenceDetail, { kind: "segment" }>,
  dataTypes: CollectionItem[]
) {
  const segmentCode = detail.code.toUpperCase();
  if (!segmentCode) return html;

  const fieldRows = new Map(
    (detail.fields ?? []).map((field) => [getFieldAnchorId(field.code), field])
  );
  const dataTypeLinks = new Map(dataTypes.map((dataType) => [dataType.code, dataType]));
  const headingPattern = new RegExp(
    `<h3\\b([^>]*\\sid=(["'])(${escapeRegExp(segmentCode)}-\\d+)\\2[^>]*)>([\\s\\S]*?)<\\/h3>`,
    "gi"
  );

  return html.replace(headingPattern, (match, _attrs, _quote, rawId, headingHtml) => {
    const rows = getFieldIdsCoveredByHeading(rawId, headingHtml)
      .map((fieldId) => fieldRows.get(fieldId))
      .filter((row): row is FieldRow => Boolean(row));

    if (!rows.length) return match;
    return buildArticleFieldQuickMetaHtml(rows, dataTypeLinks, withAppBasePath, {
      headingHtml,
      headingId: rawId
    });
  });
}

function parseRoute(path: string): AppRoute {
  const { pathname: rawPathname, hash } = splitPathAndHash(path);
  const pathname = getCleanPathFromLegacyPath(stripAppBasePath(rawPathname));
  const parts = pathname.split("/").filter(Boolean);

  if (!parts.length) {
    return { pathname: "/", hash, section: "home", page: "home" };
  }

  if (pathname === "/articles") {
    return { pathname, hash, section: "articles", page: "article-index" };
  }

  if (parts[0] === "articles" && parts[1]) {
    const articlePath = `/articles/${parts[1].toLowerCase()}`;
    const article = articlesByRoute.get(articlePath);
    return {
      pathname: articlePath,
      hash,
      section: "articles",
      page: article ? "article" : "article-index",
      article
    };
  }

  if (pathname === "/segments") {
    return {
      pathname,
      hash,
      section: "segments",
      page: segmentGuideArticle ? "article" : "reference",
      article: segmentGuideArticle
    };
  }

  if (parts[0] === "segments" && parts[1]) {
    const code = routeSlugToCode(parts[1]);
    return {
      pathname: `/segments/${parts[1].toLowerCase()}`,
      hash,
      section: "segments",
      page: segmentArticlesByCode.has(code) ? "article" : "reference",
      referenceKind: "segment",
      code,
      article: segmentArticlesByCode.get(code)
    };
  }

  if (parts[0] === "messages") {
    if (parts[1]) {
      const code = routeSlugToCode(parts[1]);
      return {
        pathname: `/messages/${parts[1].toLowerCase()}`,
        hash,
        section: "messages",
        page: messageArticlesByCode.has(code) ? "article" : "reference",
        referenceKind: "message",
        code,
        article: messageArticlesByCode.get(code)
      };
    }

    return {
      pathname: "/messages",
      hash,
      section: "messages",
      page: messageGuideArticle ? "article" : "reference",
      article: messageGuideArticle
    };
  }

  if (parts[0] === "datatypes") {
    if (parts[1]) {
      const code = routeSlugToCode(parts[1]);
      return {
        pathname: `/datatypes/${parts[1].toLowerCase()}`,
        hash,
        section: "datatypes",
        page: datatypeArticlesByCode.has(code) ? "article" : "reference",
        referenceKind: "datatype",
        code,
        article: datatypeArticlesByCode.get(code)
      };
    }

    return {
      pathname: "/datatypes",
      hash,
      section: "datatypes",
      page: datatypeGuideArticle ? "article" : "reference",
      article: datatypeGuideArticle
    };
  }

  if (parts[0] === "tables") {
    if (parts[1] === "list") {
      return {
        pathname: "/tables/list",
        hash,
        section: "tables",
        page: "reference"
      };
    }

    return parts[1]
      ? {
          pathname: `/tables/${parts[1].toLowerCase()}`,
          hash,
          section: "tables",
          page: "reference",
          referenceKind: "table",
          code: routeSlugToCode(parts[1])
        }
      : {
          pathname: "/tables",
          hash,
          section: "tables",
          page: tableGuideArticle ? "article" : "reference",
          article: tableGuideArticle
        };
  }

  if (parts[0] === "fields" && parts[1]) {
    const fieldCode = routeSlugToCode(parts[1]);
    const segmentCode = getSegmentCodeFromFieldCode(fieldCode);
    return {
      pathname: `/segments/${codeToRouteSlug(segmentCode)}`,
      hash: hash || `#${getFieldAnchorId(fieldCode)}`,
      section: "segments",
      page: segmentArticlesByCode.has(segmentCode) ? "article" : "reference",
      referenceKind: "segment",
      code: segmentCode,
      article: segmentArticlesByCode.get(segmentCode)
    };
  }

  return { pathname: "/", hash, section: "home", page: "home" };
}

function getCurrentRoute() {
  return parseRoute(`${stripAppBasePath(window.location.pathname)}${window.location.hash}`);
}

function collectionHasPath(collections: Record<ReferenceKind, CollectionItem[]>, path: string) {
  const kind = getKindFromPath(path);
  return kind ? collections[kind].some((item) => item.path === path) : false;
}

function setHashPath(path: string) {
  window.history.replaceState(null, "", `#item=${encodeURIComponent(path)}`);
}

function getInitialTheme(): "light" | "dark" {
  const stored = window.localStorage.getItem("reference-guide-theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredPanelOpen(storageKey: string, defaultOpen: boolean) {
  const stored = window.localStorage.getItem(storageKey);
  if (stored === "true") return true;
  if (stored === "false") return false;
  return defaultOpen;
}

function getStoredFieldsPanelOpen(defaultOpen: boolean) {
  return getStoredPanelOpen(fieldsPanelStorageKey, defaultOpen);
}

function getStoredComponentsPanelOpen(defaultOpen: boolean) {
  return getStoredPanelOpen(componentsPanelStorageKey, defaultOpen);
}

function getStoredStructurePanelOpen(defaultOpen: boolean) {
  return getStoredPanelOpen(structurePanelStorageKey, defaultOpen);
}

function getUnsupportedReferenceMessage(route: AppRoute, versionId: string, displayCode?: string) {
  if (!route.referenceKind || !route.code) return "";
  const label = kindSingularLabels[route.referenceKind];
  return `HL7 ${label} ${displayCode || route.code} is not supported in HL7 v${versionId}. Try a different version from the selector above.`;
}

function getBrowserTitle(section: SiteSection) {
  switch (section) {
    case "articles":
      return "Articles";
    case "segments":
      return "Segments";
    case "messages":
      return "Messages";
    case "datatypes":
      return "Datatypes";
    case "tables":
      return "Tables";
    case "fields":
      return "Fields";
    case "home":
      return "Guide";
  }
}

function getSearchPlaceholder(section: SiteSection) {
  switch (section) {
    case "articles":
      return "Search articles";
    case "segments":
      return "Search PID, OBX, OBR";
    case "messages":
      return "Search ADT_A01, ORU_R01";
    case "datatypes":
      return "Search CX, XPN, CWE";
    case "tables":
      return "Search table 0001, 0076";
    case "fields":
      return "Search MSH.9, PID.3";
    default:
      return "Search HL7 reference";
  }
}

function getPageMeta(route: AppRoute, detail: ReferenceDetail | null) {
  if (route.page === "home") {
    return {
      title: "HL7 Reference Guide | Integration Soup",
      description: homeArticleDescription
    };
  }

  if (route.article) {
    return {
      title: `${route.article.title} | Integration Soup HL7 Reference Guide`,
      description: route.article.description
    };
  }

  if (detail) {
    return {
      title: `HL7 ${detail.code} ${detail.name} | Integration Soup HL7 Reference Guide`,
      description: detail.description
    };
  }

  if (route.page === "article-index") {
    return {
      title: "HL7 Articles | Integration Soup HL7 Reference Guide",
      description: "Practical Integration Soup articles for working with HL7 interfaces."
    };
  }

  return {
    title: "Integration Soup HL7 Reference Guide",
    description:
      "Integration Soup HL7 reference guide for practical articles, segments, messages, datatypes, tables, and generated HL7 v2 reference data."
  };
}

function setMetaContent(name: string, content: string) {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonicalHref(href: string) {
  let element = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = href;
}

function App() {
  const [route, setRoute] = useState<AppRoute>(getCurrentRoute);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<VersionSummary | null>(null);
  const [searchIndex, setSearchIndex] = useState<SearchHit[]>([]);
  const [collections, setCollections] = useState<Record<ReferenceKind, CollectionItem[]>>({
    message: [],
    segment: [],
    field: [],
    datatype: [],
    table: []
  });
  const [detail, setDetail] = useState<ReferenceDetail | null>(null);
  const [articleHtml, setArticleHtml] = useState("");
  const [query, setQuery] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [articleError, setArticleError] = useState<string | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">(getInitialTheme);
  const [referenceDataReady, setReferenceDataReady] = useState(false);
  const activeModule = catalog?.modules[0] ?? null;
  const selectedPath = useMemo(() => {
    if (!route.referenceKind || !route.code || !selectedVersion?.id) return "";
    const lookupCode =
      route.referenceKind === "message" && route.article?.structureCode
        ? route.article.structureCode
        : route.code;
    const matchingItem = collections[route.referenceKind].find(
      (item) => item.code.toUpperCase() === lookupCode.toUpperCase()
    );
    return matchingItem?.path ?? "";
  }, [collections, route, selectedVersion?.id]);
  const articleSourcePath = route.article?.sourcePath ?? (route.page === "home" ? homeArticlePath : "");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("reference-guide-theme", theme);
  }, [theme]);

  const navigate = useCallback((target: string, replace = false) => {
    if (!target) return;

    if (/^(?:https?:|mailto:|tel:)/i.test(target)) {
      window.location.href = target;
      return;
    }

    if (target.startsWith("/data/")) {
      const referenceRoute = getRouteForReferencePath(target);
      if (!referenceRoute) return;
      target = referenceRoute;
    }

    if (target.startsWith("#")) {
      const nextUrl = `${window.location.pathname}${target}`;
      window.history[replace ? "replaceState" : "pushState"](null, "", nextUrl);
      setRoute(parseRoute(`${stripAppBasePath(window.location.pathname)}${target}`));
      scrollToHashTarget(target);
      return;
    }

    const { pathname, hash } = splitPathAndHash(target);
    const cleanPath = getCleanPathFromLegacyPath(pathname);
    const nextUrl = `${withAppBasePath(cleanPath)}${hash}`;
    window.history[replace ? "replaceState" : "pushState"](null, "", nextUrl);
    setRoute(parseRoute(`${cleanPath}${hash}`));
    if (!hash) {
      scrollToPageTop();
    }
    if (hash && cleanPath === stripAppBasePath(window.location.pathname)) {
      scrollToHashTarget(hash);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadCatalog(controller.signal)
      .then((nextCatalog) => {
        setCatalog(nextCatalog);
        const firstModule = nextCatalog.modules[0];
        const hashVersion = getVersionFromPath(getHashPath());
        const initialVersion =
          firstModule?.versions.find((version) => version.id === hashVersion) ??
          firstModule?.versions.find((version) => version.id === defaultVersionId) ??
          firstModule?.versions[0] ??
          null;
        setSelectedVersion(initialVersion);

        const legacyHashPath = getHashPath();
        if (legacyHashPath) {
          const cleanRoute = getRouteForReferencePath(legacyHashPath);
          if (cleanRoute) {
            navigate(cleanRoute, true);
          }
          return;
        }

        const currentPath = stripAppBasePath(window.location.pathname);
        const cleanPath = getCleanPathFromLegacyPath(currentPath);
        if (cleanPath !== currentPath) {
          navigate(`${cleanPath}${window.location.hash}`, true);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoadError(error instanceof Error ? error.message : "Unable to load catalog.");
        }
      });
    return () => controller.abort();
  }, [navigate]);

  useEffect(() => {
    const syncRoute = () => {
      const legacyHashPath = getHashPath();
      if (legacyHashPath) {
        const hashVersion = getVersionFromPath(legacyHashPath);
        const nextVersion = activeModule?.versions.find((version) => version.id === hashVersion);
        if (nextVersion) {
          setSelectedVersion(nextVersion);
        }

        const cleanRoute = getRouteForReferencePath(legacyHashPath);
        if (cleanRoute) {
          navigate(cleanRoute, true);
        }
        return;
      }

      setRoute(getCurrentRoute());
    };

    window.addEventListener("popstate", syncRoute);
    window.addEventListener("hashchange", syncRoute);
    return () => {
      window.removeEventListener("popstate", syncRoute);
      window.removeEventListener("hashchange", syncRoute);
    };
  }, [activeModule?.versions, navigate]);

  useEffect(() => {
    if (!selectedVersion) return;
    const controller = new AbortController();
    setLoadError(null);
    setManifest(null);
    setSearchIndex([]);
    setCollections({ message: [], segment: [], field: [], datatype: [], table: [] });
    setReferenceDataReady(false);

    loadManifest(selectedVersion.manifestPath, controller.signal)
      .then(async (nextManifest) => {
        setManifest(nextManifest);
        const [nextSearchIndex, nextCollections] = await Promise.all([
          loadSearchIndex(nextManifest.searchIndexPath, controller.signal),
          loadCollections(nextManifest.collections, controller.signal)
        ]);
        if (controller.signal.aborted) return;
        setSearchIndex(nextSearchIndex.filter((item) => item.kind !== "field"));
        setCollections(nextCollections);
        setReferenceDataReady(true);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setReferenceDataReady(false);
          setLoadError(error instanceof Error ? error.message : "Unable to load reference data.");
        }
      });
    return () => controller.abort();
  }, [selectedVersion]);

  useEffect(() => {
    if (!selectedPath) {
      setDetail(null);
      setDetailError(
        route.referenceKind && route.code && selectedVersion?.id && referenceDataReady
          ? getUnsupportedReferenceMessage(
              route,
              selectedVersion.id,
              route.article?.messageCode || route.article?.segmentCode || route.article?.datatypeCode
            )
          : null
      );
      return;
    }

    const controller = new AbortController();
    setDetailError(null);
    setDetail(null);

    loadReference(selectedPath, controller.signal)
      .then((nextDetail) => {
        if (controller.signal.aborted) return;
        setDetail(nextDetail);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setDetailError(error instanceof Error ? error.message : "Unable to load item.");
        }
      });
    return () => controller.abort();
  }, [referenceDataReady, route, selectedPath, selectedVersion?.id]);

  useEffect(() => {
    if (!articleSourcePath) {
      setArticleHtml("");
      setArticleError(null);
      return;
    }

    const controller = new AbortController();
    setArticleHtml("");
    setArticleError(null);

    fetch(withAppBasePath(articleSourcePath), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load ${articleSourcePath}: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        if (!controller.signal.aborted) {
          setArticleHtml(prefixRootRelativeHtmlUrls(html));
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setArticleError(error instanceof Error ? error.message : "Unable to load article.");
        }
      });

    return () => controller.abort();
  }, [articleSourcePath]);

  useEffect(() => {
    if (!route.hash || route.hash === "#fields") return;
    scrollToHashTarget(route.hash);
  }, [articleHtml, detail, route.hash]);

  useEffect(() => {
    const meta = getPageMeta(route, detail);
    document.title = meta.title;
    setMetaContent("description", meta.description);
    setCanonicalHref(`${articleData.baseUrl}${route.pathname}`);
  }, [detail, route]);

  const filteredResults = useMemo<BrowserResult[]>(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (route.section === "articles") {
      return articleList
        .map(getArticleBrowserResult)
        .filter((item) => browserResultMatchesQuery(item, normalizedQuery));
    }

    const browserKind = getReferenceKindFromRouteSection(route.section);
    const source = browserKind
      ? normalizedQuery
        ? searchIndex.filter((item) => item.kind === browserKind)
        : collections[browserKind].map<SearchHit>((item) => ({
            ...item,
            summary: "",
            tags: []
          }))
      : normalizedQuery
        ? searchIndex
        : [
            ...(segmentGuideArticle
              ? [
                  {
                    kind: "segment" as const,
                    code: "Segments",
                    title: segmentGuideArticle.title,
                    summary: segmentGuideArticle.description,
                    path: segmentGuideArticle.route
                  }
                ]
              : []),
            ...articleList.map<SearchHit>((article) => ({
              kind: "message" as const,
              code: "Article",
              title: article.title,
              summary: article.description,
              path: article.route
            })),
            ...kindOrder.flatMap((kind) =>
              collections[kind].slice(0, 12).map<SearchHit>((item) => ({
                ...item,
                summary: "",
                tags: []
              }))
            )
          ];

    const matches = source
      .filter((item) => {
        if (!normalizedQuery) return true;
        const haystack = [
          item.kind,
          item.code,
          item.title,
          item.summary,
          ...(item.tags ?? [])
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      });

    const rankedMatches = normalizedQuery
      ? [...matches].sort((left, right) => compareSearchHits(left, right, normalizedQuery))
      : matches;

    const referenceResults = rankedMatches
      .slice(0, browserKind === "field" || !browserKind ? 80 : rankedMatches.length)
      .map((item) => ({
        id: `${item.kind}-${item.path}`,
        path: item.path.startsWith("/data/") ? getRouteForReferencePath(item.path) : item.path,
        code: item.code,
        title: item.title,
        summary: item.summary,
        kind: item.kind
      }));

    const homeArticleResults =
      normalizedQuery && !browserKind
        ? articleList
            .map(getArticleBrowserResult)
            .filter((item) => browserResultMatchesQuery(item, normalizedQuery))
        : [];

    return [...referenceResults, ...homeArticleResults];
  }, [collections, query, route.section, searchIndex]);

  const selectReferencePath = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  if (loadError) {
    return <ErrorScreen message={loadError} />;
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <a className="brand-mark" href={integrationSoupHomeUrl} aria-label="Integration Soup website">
            <img src={withAppBasePath("/branding/integrationsouplogoonly.svg")} alt="" />
          </a>
          <a
            className="brand-title-link"
            href={withAppBasePath("/")}
            onClick={(event) => {
              event.preventDefault();
              navigate("/");
            }}
          >
            <p className="eyebrow">Integration Soup</p>
            <h1>
              HL7<sup className="trademark-symbol">®</sup> Reference Guide
            </h1>
          </a>
        </div>

        <nav className="section-nav" aria-label="Guide sections">
          {navItems.map((item) => (
            <a
              key={item.section}
              className={route.section === item.section ? "section-nav-link active" : "section-nav-link"}
              href={withAppBasePath(item.path)}
              onClick={(event) => {
                event.preventDefault();
                navigate(item.path);
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="topbar-controls">
          <button
            className="theme-toggle"
            type="button"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          >
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <div className="version-picker">
            <a
              className="version-picker-link"
              href={withAppBasePath(versionGuidePath)}
              onClick={(event) => {
                event.preventDefault();
                navigate(versionGuidePath);
              }}
            >
              Version
            </a>
            <select
              aria-label="HL7 version"
              value={selectedVersion?.id ?? ""}
              onChange={(event) => {
                const nextVersion = activeModule?.versions.find(
                  (version) => version.id === event.target.value
                );
                setSelectedVersion(nextVersion ?? null);
              }}
            >
              {activeModule?.versions.map((version) => (
                <option key={version.id} value={version.id}>
                  {version.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="workspace">
        <aside className="browser-panel" aria-label="Guide browser">
          <section className="search-panel">
            <div className="browser-heading">
              <p className="eyebrow">Browsing</p>
              <h2>{getBrowserTitle(route.section)}</h2>
            </div>
            <label className="search-box">
              <Search size={18} />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={getSearchPlaceholder(route.section)}
              />
            </label>
          </section>

          <section className="result-list" aria-label="Guide links">
            {filteredResults.map((result) => (
              <ResultButton
                key={result.id}
                result={result}
                active={result.path === route.pathname}
                onSelect={navigate}
              />
            ))}
            {!filteredResults.length && (
              <div className="empty-state">
                <Search size={18} />
                <p>No matching reference items.</p>
              </div>
            )}
          </section>
        </aside>

        <section className="detail-panel" aria-live="polite">
          {route.page === "home" && (
            <HomePage
              html={articleHtml}
              articleError={articleError}
              catalog={catalog}
              onNavigate={navigate}
            />
          )}
          {route.page === "article-index" && (
            <ArticleIndexPage articles={articleList} onNavigate={navigate} />
          )}
          {route.page === "article" && route.article && (
            <ArticlePage
              article={route.article}
              html={articleHtml}
              articleError={articleError}
              detail={detail}
              detailError={detailError}
              manifest={manifest}
              catalog={catalog}
              collections={collections}
              selectedPath={selectedPath}
              openFields={route.hash === "#fields"}
              openComponents={route.hash === "#components"}
              openStructure={route.hash === "#structure"}
              onNavigate={navigate}
              onSelect={selectReferencePath}
            />
          )}
          {route.page === "reference" && (
            <>
              {detailError && <InlineError message={detailError} />}
              {!detail && !detailError && <ReferenceLanding route={route} collections={collections} onNavigate={navigate} />}
              {detail && (
                <DetailView
                  detail={detail}
                  manifest={manifest}
                  catalog={catalog}
                  collections={collections}
                  selectedPath={selectedPath}
                  onSelect={selectReferencePath}
                />
              )}
            </>
          )}
        </section>

        <aside className="context-panel" aria-label="Data context">
          <SourceSummary manifest={manifest} catalog={catalog} activeModuleName={activeModule?.name} />
        </aside>
      </main>
    </div>
  );
}

async function loadCollections(collections: CollectionSummary[], signal?: AbortSignal) {
  const entries = await Promise.all(
    collections
      .filter((collection) => collection.kind !== "field")
      .map(async (collection) => {
        const items = await loadCollection(collection.itemsPath, signal);
        return [collection.kind, items] as const;
      })
  );

  return entries.reduce<Record<ReferenceKind, CollectionItem[]>>(
    (acc, [kind, items]) => {
      acc[kind] = items;
      return acc;
    },
    { message: [], segment: [], field: [], datatype: [], table: [] }
  );
}

function HomePage({
  html,
  articleError,
  catalog,
  onNavigate
}: {
  html: string;
  articleError: string | null;
  catalog: Catalog | null;
  onNavigate: (path: string) => void;
}) {
  return (
    <article className="reference-detail article-page">
      {articleError && <InlineError message={articleError} />}
      {!html && !articleError && <DetailSkeleton />}
      {html && <ArticleBody html={html} onNavigate={onNavigate} />}
      <footer className="detail-footer">
        <ShieldCheck size={16} />
        <span>{catalog?.legal.shortNotice}</span>
      </footer>
    </article>
  );
}

function ArticleIndexPage({
  articles,
  onNavigate
}: {
  articles: ArticleRecord[];
  onNavigate: (path: string) => void;
}) {
  return (
    <article className="reference-detail article-index-page">
      <div className="detail-header">
        <div className="detail-title-group">
          <span className="kind-chip">Articles</span>
          <h2>
            <span>HL7 Articles</span>
            <small>Practical guides from real interface work.</small>
          </h2>
        </div>
      </div>
      <div className="article-card-grid">
        {articles.map((article) => {
          const iconKey = getArticleIconKey(article);
          const Icon = articleIcons[iconKey];

          return (
            <a
              className={`article-card-link article-card-link--with-icon article-card-link--${iconKey}`}
              href={withAppBasePath(article.route)}
              key={article.id}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(article.route);
              }}
            >
              <span className="article-card-icon" aria-hidden="true">
                <Icon size={19} strokeWidth={1.9} />
              </span>
              <span className="article-card-copy">
                <strong>{article.title}</strong>
                <span className="article-card-description">{article.description}</span>
              </span>
            </a>
          );
        })}
      </div>
    </article>
  );
}

function ReferenceLanding({
  route,
  collections,
  onNavigate
}: {
  route: AppRoute;
  collections: Record<ReferenceKind, CollectionItem[]>;
  onNavigate: (path: string) => void;
}) {
  const kind = getReferenceKindFromRouteSection(route.section);
  if (!kind || route.code) {
    return <DetailSkeleton />;
  }

  const title = kindLabels[kind];
  const items = collections[kind];
  const Icon = kindIcons[kind];
  const itemKindLabel = singularKindLabel(kind);
  const isFullTableList = route.pathname === "/tables/list";
  const visibleItems = isFullTableList ? items : items.slice(0, 120);
  const heading = isFullTableList ? "All HL7 Tables" : `HL7 ${title}`;
  const subtitle = isFullTableList
    ? "Definitions, table type, HL7 Soup shortcuts, and value-list links for the HL7 tables in this reference set."
    : "Select an item from the list to inspect the reference.";
  const listClassName = isFullTableList
    ? "collection-link-list table-reference-list"
    : "collection-link-grid";

  return (
    <article className="reference-detail collection-landing-page">
      <div className="detail-header">
        <div className="detail-title-group">
          <span className="kind-chip">{title}</span>
          <h2>
            <span>{heading}</span>
            <small>{subtitle}</small>
          </h2>
        </div>
      </div>
      <div className={listClassName}>
        {isFullTableList && (
          <div className="table-reference-grid-header" aria-hidden="true">
            <span>Table</span>
            <span>Name</span>
            <span>Type</span>
            <span>HL7 Soup</span>
            <span>Description</span>
            <span />
          </div>
        )}
        {visibleItems.map((item) => {
          const fullLabel = `${itemKindLabel} ${item.code}: ${item.title}${item.type ? ` - ${item.type}` : ""}${
            item.hl7SoupReference ? ` - ${item.hl7SoupReference}` : ""
          }${item.description ? ` - ${item.description}` : ""}`;
          return (
            <button
              className={`related-link collection-link-card${isFullTableList ? " table-reference-row" : ""}`}
              key={item.path}
              type="button"
              aria-label={fullLabel}
              title={isFullTableList ? fullLabel : undefined}
              onClick={() => onNavigate(item.path)}
            >
              {isFullTableList ? (
                <>
                  <span className="table-reference-code">{item.code}</span>
                  <span className="table-reference-name">
                    <strong>{item.title}</strong>
                  </span>
                  <span className="table-reference-type">{item.type ?? ""}</span>
                  <span className="table-reference-source">
                    {item.hl7SoupReference ? (
                      <Hl7SoupReference reference={item.hl7SoupReference} focusable={false} />
                    ) : (
                      ""
                    )}
                  </span>
                  <span className="table-reference-description">{item.description ?? ""}</span>
                </>
              ) : (
                <>
                  <span className="collection-link-mark" aria-hidden="true">
                    <Icon size={17} />
                  </span>
                  <span className="collection-link-copy">
                    <small className="collection-link-kicker">
                      {itemKindLabel} {item.code}
                    </small>
                    <span className="collection-link-heading">
                      <strong>{item.title}</strong>
                    </span>
                    {item.description && (
                      <span className="collection-link-description">{item.description}</span>
                    )}
                  </span>
                </>
              )}
              <ChevronRight
                className="collection-link-arrow table-reference-arrow"
                size={17}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    </article>
  );
}

function ArticlePage({
  article,
  html,
  articleError,
  detail,
  detailError,
  manifest,
  catalog,
  collections,
  selectedPath,
  openFields,
  openComponents,
  openStructure,
  onNavigate,
  onSelect
}: {
  article: ArticleRecord;
  html: string;
  articleError: string | null;
  detail: ReferenceDetail | null;
  detailError: string | null;
  manifest: Manifest | null;
  catalog: Catalog | null;
  collections: Record<ReferenceKind, CollectionItem[]>;
  selectedPath: string;
  openFields: boolean;
  openComponents: boolean;
  openStructure: boolean;
  onNavigate: (path: string) => void;
  onSelect: (path: string) => void;
}) {
  const messageDetail = detail?.kind === "message" ? detail : null;
  const segmentDetail = detail?.kind === "segment" ? detail : null;
  const datatypeDetail = detail?.kind === "datatype" ? detail : null;
  const afterHeader = messageDetail ? (
    <MessageStructurePanel
      detail={messageDetail}
      manifest={manifest}
      selectedPath={selectedPath}
      defaultOpen={openStructure || article.structurePanelDefaultOpen === true}
      onSelect={onSelect}
    />
  ) : detailError && article.messageCode ? (
    <InlineError message={detailError} />
  ) : segmentDetail ? (
      <SegmentReferencePanel
        detail={segmentDetail}
        manifest={manifest}
        collections={collections}
        selectedPath={selectedPath}
        defaultOpen={openFields || article.fieldsPanelDefaultOpen === true}
        onSelect={onSelect}
      />
  ) : detailError && article.segmentCode ? (
    <InlineError message={detailError} />
  ) : datatypeDetail ? (
    <DatatypeReferencePanel
      detail={datatypeDetail}
      manifest={manifest}
      selectedPath={selectedPath}
      defaultOpen={article.datatypeCode ? true : openComponents}
      onSelect={onSelect}
    />
  ) : detailError && article.datatypeCode ? (
    <InlineError message={detailError} />
  ) : null;
  const enhancedHtml =
    article.segmentCode && segmentDetail
      ? addSegmentFieldMetadataToArticleHtml(html, segmentDetail, collections.datatype)
      : html;

  return (
    <article className="reference-detail article-page">
      {articleError && <InlineError message={articleError} />}
      {!html && !articleError && <DetailSkeleton />}
      {html && (
        <ArticleBody html={enhancedHtml} afterHeader={afterHeader} onNavigate={onNavigate} />
      )}
      <footer className="detail-footer">
        <ShieldCheck size={16} />
        <span>{catalog?.legal.shortNotice}</span>
      </footer>
    </article>
  );
}

function ArticleBody({
  html,
  afterHeader,
  onNavigate
}: {
  html: string;
  afterHeader?: React.ReactNode;
  onNavigate: (path: string) => void;
}) {
  const { headerHtml, bodyHtml } = splitArticleHtml(html);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) {
      prepareClickableHl7Samples(bodyRef.current);
    }
  });

  return (
    <div
      className="article-body"
      ref={bodyRef}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        const anchor = target.closest("a[href]");
        if (!anchor) return;

        const href = anchor.getAttribute("href") ?? "";
        if (!href || /^(?:https?:|mailto:|tel:)/i.test(href)) return;

        event.preventDefault();
        onNavigate(href);
      }}
    >
      {headerHtml && <div dangerouslySetInnerHTML={{ __html: headerHtml }} />}
      {afterHeader}
      <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
    </div>
  );
}

function splitArticleHtml(html: string) {
  const match = html.match(/^\s*(<header\b[\s\S]*?<\/header>)([\s\S]*)$/i);
  return {
    headerHtml: match?.[1] ?? "",
    bodyHtml: match?.[2] ?? html
  };
}

function toBase64Url(text: string) {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function buildHl7SoupLink(sample: HTMLElement) {
  const text = sample.textContent?.replace(/\r?\n/g, "\r").trim() ?? "";
  const title = sample.getAttribute("data-hl7-sample-title") || "HL7 Message.hl7";
  return `${hl7SoupWebUrl}/#open=hl7&title=${encodeURIComponent(title)}&data=${toBase64Url(text)}`;
}

function hasSelectedText() {
  const selection = window.getSelection ? window.getSelection() : null;
  return !!selection && selection.toString().length > 0;
}

function openHl7Sample(sample: HTMLElement) {
  const href = sample.getAttribute("data-hl7-soup-href") || buildHl7SoupLink(sample);
  const opened = window.open(href, "_blank", "noopener");

  if (opened) {
    opened.opener = null;
  }
}

function prepareClickableHl7Samples(root: HTMLElement) {
  const wrappers = root.querySelectorAll<HTMLElement>(".hl7-sample-wrap");

  wrappers.forEach((wrapper) => {
    const sample = wrapper.querySelector<HTMLElement>(".hl7-sample");
    const link = wrapper.querySelector<HTMLAnchorElement>(".hl7-sample-open");

    if (!sample || !link) return;

    const href = buildHl7SoupLink(sample);
    link.href = href;
    sample.setAttribute("data-hl7-soup-href", href);
    sample.setAttribute("role", "link");
    sample.setAttribute("tabindex", "0");
    sample.setAttribute("title", "Open this HL7 sample in HL7 Soup Web");

    if (sample.getAttribute("data-hl7-click-ready") === "true") return;

    sample.setAttribute("data-hl7-click-ready", "true");
    sample.addEventListener("click", () => {
      if (!hasSelectedText()) {
        openHl7Sample(sample);
      }
    });
    sample.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openHl7Sample(sample);
      }
    });
  });
}

function MessageStructurePanel({
  detail,
  manifest,
  selectedPath,
  defaultOpen,
  onSelect
}: {
  detail: Extract<ReferenceDetail, { kind: "message" }>;
  manifest: Manifest | null;
  selectedPath: string;
  defaultOpen: boolean;
  onSelect: (path: string) => void;
}) {
  const [open, setOpen] = useState(() => getStoredStructurePanelOpen(defaultOpen));

  useEffect(() => {
    setOpen(getStoredStructurePanelOpen(defaultOpen));
  }, [defaultOpen, detail.code]);

  const handleToggle = useCallback((event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const nextOpen = event.currentTarget.open;
    setOpen(nextOpen);
    window.localStorage.setItem(structurePanelStorageKey, String(nextOpen));
  }, []);

  return (
    <section className="segment-reference-panel message-structure-panel" id="structure">
      <details open={open} onToggle={handleToggle}>
        <summary>
          <span>
            <strong>HL7 message structure</strong>
            <small>
              {detail.code} groups and segments from {manifest?.label ?? `HL7 v${detail.version}`}
            </small>
          </span>
          <span className="reference-summary-action">{open ? "Hide structure" : "Show structure"}</span>
        </summary>
        <div className="segment-reference-body">
          <div className="reference-panel-topline">
            <p>
              These are the generated groups and segments for the version selected at the top of
              the page. The article explains the workflow, and this panel follows the chosen HL7
              version.
            </p>
            <a className="icon-link secondary" href={withAppBasePath(selectedPath)} title="Open raw JSON">
              <Braces size={18} />
              <span>JSON</span>
            </a>
          </div>
          <MetadataGrid detail={detail} manifest={manifest} />
          <StructureTable rows={detail.structure ?? []} onSelect={onSelect} />
        </div>
      </details>
    </section>
  );
}

function SegmentReferencePanel({
  detail,
  manifest,
  collections,
  selectedPath,
  defaultOpen,
  onSelect
}: {
  detail: Extract<ReferenceDetail, { kind: "segment" }>;
  manifest: Manifest | null;
  collections: Record<ReferenceKind, CollectionItem[]>;
  selectedPath: string;
  defaultOpen: boolean;
  onSelect: (path: string) => void;
}) {
  const [open, setOpen] = useState(() => getStoredFieldsPanelOpen(defaultOpen));

  useEffect(() => {
    setOpen(getStoredFieldsPanelOpen(defaultOpen));
  }, [defaultOpen, detail.code]);

  const handleToggle = useCallback((event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const nextOpen = event.currentTarget.open;
    setOpen(nextOpen);
    window.localStorage.setItem(fieldsPanelStorageKey, String(nextOpen));
  }, []);

  return (
    <section className="segment-reference-panel" id="fields">
      <details
        open={open}
        onToggle={handleToggle}
      >
        <summary>
          <span>
            <strong>HL7 field reference</strong>
            <small>
              {detail.code} fields from {manifest?.label ?? `HL7 v${detail.version}`}
            </small>
          </span>
          <span className="reference-summary-action">{open ? "Hide fields" : "Show fields"}</span>
        </summary>
        <div className="segment-reference-body">
          <div className="reference-panel-topline">
            <p>
              These are the generated fields for the version selected at the top of the page. The
              document stays the same, but the reference panel follows that version.
            </p>
            <a className="icon-link secondary" href={withAppBasePath(selectedPath)} title="Open raw JSON">
              <Braces size={18} />
              <span>JSON</span>
            </a>
          </div>
          <MetadataGrid detail={detail} manifest={manifest} />
          <FieldsTable
            rows={detail.fields ?? []}
            dataTypes={collections.datatype}
            onSelect={onSelect}
            fieldLinks="page-anchor"
          />
        </div>
      </details>
    </section>
  );
}

function DatatypeReferencePanel({
  detail,
  manifest,
  selectedPath,
  defaultOpen,
  onSelect
}: {
  detail: Extract<ReferenceDetail, { kind: "datatype" }>;
  manifest: Manifest | null;
  selectedPath: string;
  defaultOpen: boolean;
  onSelect: (path: string) => void;
}) {
  const [open, setOpen] = useState(() => getStoredComponentsPanelOpen(defaultOpen));

  useEffect(() => {
    setOpen(getStoredComponentsPanelOpen(defaultOpen));
  }, [defaultOpen, detail.code]);

  const handleToggle = useCallback((event: React.SyntheticEvent<HTMLDetailsElement>) => {
    const nextOpen = event.currentTarget.open;
    setOpen(nextOpen);
    window.localStorage.setItem(componentsPanelStorageKey, String(nextOpen));
  }, []);

  return (
    <section className="segment-reference-panel" id="components">
      <details open={open} onToggle={handleToggle}>
        <summary>
          <span>
            <strong>HL7 datatype components</strong>
            <small>
              {detail.code} components from {manifest?.label ?? `HL7 v${detail.version}`}
            </small>
          </span>
          <span className="reference-summary-action">{open ? "Hide components" : "Show components"}</span>
        </summary>
        <div className="segment-reference-body">
          <div className="reference-panel-topline">
            <p>
              These are the generated components for the version selected at the top of the page.
              The article stays practical, and this panel follows the chosen HL7 version.
            </p>
            <a className="icon-link secondary" href={withAppBasePath(selectedPath)} title="Open raw JSON">
              <Braces size={18} />
              <span>JSON</span>
            </a>
          </div>
          <ComponentsTable
            rows={detail.components ?? []}
            onSelect={onSelect}
            componentLinks="page-anchor"
          />
        </div>
      </details>
    </section>
  );
}

function ResultButton({
  result,
  active,
  onSelect
}: {
  result: BrowserResult;
  active: boolean;
  onSelect: (path: string) => void;
}) {
  const Icon = result.kind ? kindIcons[result.kind] : BookOpen;
  return (
    <button
      className={active ? "result-button active" : "result-button"}
      type="button"
      onClick={() => onSelect(result.path)}
    >
      <span className="result-icon">
        <Icon size={16} />
      </span>
      <span className="result-body">
        <span className="result-code">{result.code}</span>
        <span className="result-title">{result.title}</span>
        {result.summary && <span className="result-summary">{result.summary}</span>}
      </span>
    </button>
  );
}

function DetailView({
  detail,
  manifest,
  catalog,
  collections,
  selectedPath,
  onSelect
}: {
  detail: ReferenceDetail;
  manifest: Manifest | null;
  catalog: Catalog | null;
  collections: Record<ReferenceKind, CollectionItem[]>;
  selectedPath: string;
  onSelect: (path: string) => void;
}) {
  const Icon = kindIcons[detail.kind];
  const detailTitle = detail.name;
  const sampleMessageResources = useMemo(() => getSampleMessageResources(detail), [detail]);

  return (
    <article className="reference-detail">
      <div className="detail-header">
        <div className="detail-title-group">
          <span className="kind-chip">
            <Icon size={15} />
            {singularKindLabel(detail.kind)}
          </span>
          <h2>
            <span>{detail.code}</span>
            {detailTitle && <small>{detailTitle}</small>}
          </h2>
        </div>
        <a className="icon-link secondary" href={withAppBasePath(selectedPath)} title="Open raw JSON">
          <Braces size={18} />
          <span>JSON</span>
        </a>
      </div>

      <DetailDescription detail={detail} />

      {detail.kind === "segment" && (
        <SourceResources featured title="Practical Guide" resources={detail.resources ?? []} />
      )}

      <MetadataGrid detail={detail} manifest={manifest} />

      {detail.kind === "message" && (
        <>
          <NarrativeSections narratives={detail.narratives ?? []} />
          <StructureTable rows={detail.structure ?? []} onSelect={onSelect} />
          <Examples examples={detail.examples ?? []} />
          <SourceResources title="Sample Messages" resources={sampleMessageResources} />
          <ReferenceLinks title="Related" links={detail.related ?? []} onSelect={onSelect} />
          <SourceResources compact resources={detail.resources ?? []} />
        </>
      )}

      {detail.kind === "segment" && (
        <>
          <FieldsTable
            rows={detail.fields ?? []}
            dataTypes={collections.datatype}
            onSelect={onSelect}
          />
          <SourceResources title="Sample Messages" resources={sampleMessageResources} />
          <ReferenceLinks title="Used By" links={detail.usedBy ?? []} onSelect={onSelect} />
        </>
      )}

      {detail.kind === "datatype" && (
        <>
          <ComponentsTable rows={detail.components ?? []} onSelect={onSelect} />
          <ReferenceLinks title="Used By" links={detail.usedBy ?? []} onSelect={onSelect} />
        </>
      )}

      {detail.kind === "table" && (
        <>
          <ValuesTable values={detail.values ?? []} />
          <ReferenceLinks title="Used By" links={detail.usedBy ?? []} onSelect={onSelect} />
        </>
      )}

      <footer className="detail-footer">
        <ShieldCheck size={16} />
        <span>{catalog?.legal.shortNotice}</span>
      </footer>
    </article>
  );
}

function DetailDescription({ detail }: { detail: ReferenceDetail }) {
  return (
    <div className="description-block">
      <p className="description">{detail.description}</p>
      {(detail.kind === "field" || detail.kind === "segment") && detail.officialDescription && (
        <p className="description official-description">
          <strong>Official description:</strong> {detail.officialDescription}
        </p>
      )}
    </div>
  );
}

function MetadataGrid({ detail, manifest }: { detail: ReferenceDetail; manifest: Manifest | null }) {
  if (detail.kind === "datatype") return null;

  const pairs: Array<[string, string | undefined]> =
    detail.kind === "table"
      ? [
          ["Table Type", detail.type],
          ["HL7 Soup Ref", detail.hl7SoupReference]
        ]
      : [
          ["Version", detail.version],
          ["Reference Set", manifest?.label],
          ["Kind", singularKindLabel(detail.kind)]
        ];

  if (detail.kind === "message") {
    pairs.push(["Message Type", detail.messageType], ["Trigger Event", detail.triggerEvent]);
  }
  if (detail.kind === "segment") {
    pairs.push(["Chapter", detail.chapter]);
  }
  if (detail.kind === "field") {
    pairs.push(
      ["Segment", detail.segment],
      ["Sequence", String(detail.sequence)],
      ["Required", getRequiredValue(detail.usage)],
      ["Repeatable", getRepeatableValue(detail.cardinality)],
      ["Data Type", detail.dataType],
      ["Table", detail.table],
      ["Length", detail.length]
    );
  }
  return (
    <dl className={`metadata-grid${detail.kind === "table" ? " table-metadata-grid" : ""}`}>
      {pairs
        .filter(([, value]) => value)
        .map(([label, value]) => (
          <div key={`${label}-${value}`}>
            <dt>{label}</dt>
            <dd>
              {label === "HL7 Soup Ref" && value ? (
                <Hl7SoupReference reference={value} />
              ) : (
                value
              )}
            </dd>
          </div>
        ))}
    </dl>
  );
}

function Hl7SoupReference({
  reference,
  focusable = true
}: {
  reference: string;
  focusable?: boolean;
}) {
  const tooltip = buildHl7SoupReferenceTooltip(reference);
  return (
    <span
      className="hl7-soup-reference-tooltip"
      data-tooltip={tooltip}
      tabIndex={focusable ? 0 : undefined}
      title={tooltip}
      aria-label={tooltip}
    >
      <code>{reference}</code>
    </span>
  );
}

function RequiredIndicator({ usage }: { usage?: string }) {
  if (!usage) return null;

  const value = getRequiredValue(usage);
  const title = getRequiredTitle(usage);
  return (
    <span className="definition-code" title={title} aria-label={`${value}: ${title}`}>
      {value}
    </span>
  );
}

function RepeatableIndicator({ cardinality }: { cardinality?: string }) {
  if (!cardinality) return null;

  const value = getRepeatableValue(cardinality);
  const title = getRepeatableTitle(cardinality);
  return (
    <span className="definition-code definition-code-muted" title={title} aria-label={`${value}: ${title}`}>
      {value}
    </span>
  );
}

function getRequiredValue(usage?: string) {
  switch (usage?.toUpperCase()) {
    case "R":
    case "RE":
      return "Yes";
    case "O":
    case "X":
      return "No";
    case "C":
    case "CE":
      return "Conditional";
    case "B":
      return "Legacy";
    case "W":
      return "Withdrawn";
    default:
      return usage;
  }
}

function getRequiredTitle(usage: string) {
  switch (usage.toUpperCase()) {
    case "R":
      return "Required";
    case "RE":
      return "Required but may be empty";
    case "O":
      return "Optional";
    case "C":
    case "CE":
      return "Conditional";
    case "X":
      return "Not used";
    case "B":
      return "Backward compatibility";
    case "W":
      return "Withdrawn";
    default:
      return usage;
  }
}

function getFieldUsageBadge(usage?: string) {
  switch (usage?.toUpperCase()) {
    case "R":
    case "RE":
      return "Required";
    case "C":
    case "CE":
      return "Conditional";
    case "X":
      return "Not used";
    case "B":
      return "Legacy";
    case "W":
      return "Withdrawn";
    default:
      return undefined;
  }
}

function getRepeatableValue(cardinality?: string) {
  if (!cardinality) return cardinality;

  const max = cardinality.split("..")[1];
  if (max === "*" || (Number.isFinite(Number(max)) && Number(max) > 1)) {
    return "Yes";
  }

  return "No";
}

function getRepeatableTitle(cardinality: string) {
  switch (cardinality) {
    case "0..1":
      return "Single";
    case "1..1":
      return "Single, required";
    case "0..*":
      return "Repeating";
    case "1..*":
      return "Repeating, required";
    default:
      return cardinality;
  }
}

function StructureTable({
  rows,
  onSelect
}: {
  rows: StructureNode[];
  onSelect: (path: string) => void;
}) {
  const groupKeys = useMemo(() => collectStructureGroupKeys(rows), [rows]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set(groupKeys));

  useEffect(() => {
    setExpandedGroups(new Set(groupKeys));
  }, [groupKeys]);

  const visibleRows = useMemo(
    () => flattenStructureRows(rows, expandedGroups),
    [expandedGroups, rows]
  );

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  return (
    <TableSection title="Message Structure">
      <table>
        <thead>
          <tr>
            <th>Segment</th>
            <th>Name</th>
            <th>Required</th>
            <th>Repeatable</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ row, key, depth, hasChildren, expanded }) => (
            <tr
              key={key}
              className={hasChildren ? "structure-group-row" : depth > 0 ? "structure-child-row" : undefined}
            >
              <td>
                <div className="structure-cell" style={{ "--structure-depth": depth } as React.CSSProperties}>
                  {hasChildren ? (
                    <button
                      className="structure-toggle"
                      type="button"
                      onClick={() => toggleGroup(key)}
                      aria-expanded={expanded}
                      title={expanded ? "Collapse group" : "Expand group"}
                    >
                      {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                  ) : (
                    <span className="structure-spacer" aria-hidden="true" />
                  )}
                  {row.path ? (
                    <button className="table-link" type="button" onClick={() => onSelect(row.path!)}>
                      {row.segment}
                    </button>
                  ) : (
                    <span className={hasChildren ? "structure-group-code" : "structure-code"}>
                      {row.segment}
                    </span>
                  )}
                </div>
              </td>
              <td>
                {hasChildren ? (
                  <button className="structure-name-toggle" type="button" onClick={() => toggleGroup(key)}>
                    {row.name}
                  </button>
                ) : (
                  row.name
                )}
              </td>
              <td>
                <RequiredIndicator usage={row.usage} />
              </td>
              <td>
                <RepeatableIndicator cardinality={row.cardinality} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </tablesection>
  );
}

interface FlattenedStructureRow {
  row: StructureNode;
  key: string;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
}

function collectStructureGroupKeys(rows: StructureNode[], parentKey = ""): string[] {
  return rows.flatMap((row, index) => {
    const key = parentKey ? `${parentKey}.${index}` : String(index);
    const children = row.children ?? [];
    if (!children.length) return [];
    return [key, ...collectStructureGroupKeys(children, key)];
  });
}

function flattenStructureRows(
  rows: StructureNode[],
  expandedGroups: Set<string>,
  depth = 0,
  parentKey = ""
): FlattenedStructureRow[] {
  return rows.flatMap((row, index) => {
    const key = parentKey ? `${parentKey}.${index}` : String(index);
    const children = row.children ?? [];
    const hasChildren = children.length > 0;
    const expanded = hasChildren && expandedGroups.has(key);
    const current = { row, key, depth, hasChildren, expanded };

    if (!expanded) {
      return [current];
    }

    return [current, ...flattenStructureRows(children, expandedGroups, depth + 1, key)];
  });
}

function FieldsTable({
  rows,
  dataTypes,
  onSelect,
  fieldLinks = "reference"
}: {
  rows: FieldRow[];
  dataTypes: CollectionItem[];
  onSelect: (path: string) => void;
  fieldLinks?: "reference" | "page-anchor";
}) {
  const dataTypeLinks = useMemo(
    () => new Map(dataTypes.map((dataType) => [dataType.code, dataType])),
    [dataTypes]
  );
  const displayRows = rows.map((row) => {
    const dataType = row.dataType ? dataTypeLinks.get(row.dataType) : null;
    const dataTypeTitle = dataType
      ? `${row.dataType}: ${dataType.title}`
      : row.dataType;
    const usageBadge = getFieldUsageBadge(row.usage);
    const isRepeatable = getRepeatableValue(row.cardinality) === "Yes";

    return { row, dataType, dataTypeTitle, usageBadge, isRepeatable };
  });

  return (
    <TableSection title="Fields" className="fields-table-section">
      <table className="fields-reference-table">
        <colgroup>
          <col className="fields-col-code" />
          <col className="fields-col-name" />
          <col className="fields-col-required" />
          <col className="fields-col-repeatable" />
          <col className="fields-col-type" />
          <col className="fields-col-table" />
        </colgroup>
        <thead>
          <tr>
            <th>Field</th>
            <th>Name</th>
            <th>Required</th>
            <th>Repeatable</th>
            <th>Type</th>
            <th>Table</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map(({ row, dataType, dataTypeTitle }) => {
            const fieldAnchorId = getFieldAnchorId(row.code);
            return (
            <tr className="field-reference-anchor" id={getFieldReferenceAnchorId(row.code)} key={row.code}>
              <td data-label="Field">
                {fieldLinks === "page-anchor" ? (
                  <a className="table-link" href={`#${fieldAnchorId}`}>
                    {row.code}
                  </a>
                ) : (
                  row.code
                )}
              </td>
              <td data-label="Name">{row.name}</td>
              <td data-label="Required">
                <RequiredIndicator usage={row.usage} />
              </td>
              <td data-label="Repeatable">
                <RepeatableIndicator cardinality={row.cardinality} />
              </td>
              <td data-label="Type">
                {dataType ? (
                  <button
                    className="table-link"
                    type="button"
                    title={dataTypeTitle}
                    onClick={() => onSelect(dataType.path)}
                  >
                    {row.dataType}
                  </button>
                ) : (
                  <span title={dataTypeTitle}>{row.dataType}</span>
                )}
              </td>
              <td data-label="Table">
                {row.tablePath ? (
                  <button className="table-link" type="button" onClick={() => onSelect(row.tablePath!)}>
                    {row.table}
                  </button>
                ) : (
                  row.table
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      <div className="fields-card-list">
        {displayRows.map(({ row, dataType, dataTypeTitle, usageBadge, isRepeatable }) => {
          const fieldAnchorId = getFieldAnchorId(row.code);
          return (
          <article className="field-card field-reference-anchor" id={`${getFieldReferenceAnchorId(row.code)}-card`} key={`${row.code}-card`}>
            <div className="field-card-header">
              {fieldLinks === "page-anchor" ? (
                <a className="table-link field-card-code" href={`#${fieldAnchorId}`}>
                  {row.code}
                </a>
              ) : (
                <strong className="field-card-code">{row.code}</strong>
              )}
              <strong className="field-card-name">{row.name}</strong>
            </div>
            <div className="field-card-meta" aria-label={`${row.code} field details`}>
              {usageBadge && row.usage && (
                <span className="field-card-pill" title={getRequiredTitle(row.usage)}>
                  {usageBadge}
                </span>
              )}
              {isRepeatable && row.cardinality && (
                <span className="field-card-pill" title={getRepeatableTitle(row.cardinality)}>
                  Repeatable
                </span>
              )}
              {row.dataType && (
                <span className="field-card-token">
                  <span>Type</span>
                  {dataType ? (
                    <button
                      className="table-link"
                      type="button"
                      title={dataTypeTitle}
                      onClick={() => onSelect(dataType.path)}
                    >
                      {row.dataType}
                    </button>
                  ) : (
                    <span title={dataTypeTitle}>{row.dataType}</span>
                  )}
                </span>
              )}
              {row.table && (
                <span className="field-card-token">
                  <span>Table</span>
                  {row.tablePath ? (
                    <button className="table-link" type="button" onClick={() => onSelect(row.tablePath!)}>
                      {row.table}
                    </button>
                  ) : (
                    row.table
                  )}
                </span>
              )}
            </div>
          </article>
          );
        })}
      </div>
    </tablesection>
  );
}

function ComponentsTable({
  rows,
  onSelect,
  componentLinks = "reference"
}: {
  rows: ComponentRow[];
  onSelect: (path: string) => void;
  componentLinks?: "reference" | "page-anchor";
}) {
  if (!rows.length) {
    return (
      <TableSection title="Components" className="components-table-section">
        <div className="empty-state table-empty-state">
          This datatype is primitive in the selected HL7 version, so there are no
          caret-separated components to expand.
        </div>
      </tablesection>
    );
  }

  return (
    <TableSection title="Components" className="components-table-section">
      <table className="components-reference-table">
        <colgroup>
          <col className="components-col-position" />
          <col className="components-col-code" />
          <col className="components-col-name" />
          <col className="components-col-type" />
          <col className="components-col-table" />
          <col className="components-col-description" />
        </colgroup>
        <thead>
          <tr>
            <th>Pos</th>
            <th>Component</th>
            <th>Name</th>
            <th>Type</th>
            <th>Table</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const componentAnchorId = getFieldAnchorId(row.code);
            return (
              <tr className="component-reference-anchor" id={getComponentReferenceAnchorId(row.code)} key={row.code}>
                <td data-label="Pos">{row.position}</td>
                <td data-label="Component">
                  {componentLinks === "page-anchor" ? (
                    <a className="table-link" href={`#${componentAnchorId}`}>
                      {row.code}
                    </a>
                  ) : (
                    row.code
                  )}
                </td>
                <td data-label="Name">{row.name}</td>
                <td data-label="Type">
                  {row.dataTypePath ? (
                    <button className="table-link" type="button" onClick={() => onSelect(row.dataTypePath!)}>
                      {row.dataType}
                    </button>
                  ) : (
                    row.dataType
                  )}
                </td>
                <td data-label="Table">
                  {row.tablePath ? (
                    <button className="table-link" type="button" onClick={() => onSelect(row.tablePath!)}>
                      {row.table}
                    </button>
                  ) : (
                    row.table
                  )}
                </td>
                <td data-label="Description">{row.description}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="components-card-list">
        {rows.map((row) => {
          const componentAnchorId = getFieldAnchorId(row.code);
          return (
            <article className="field-card component-card component-reference-anchor" id={`${getComponentReferenceAnchorId(row.code)}-card`} key={`${row.code}-card`}>
              <div className="field-card-header">
                {componentLinks === "page-anchor" ? (
                  <a className="table-link field-card-code" href={`#${componentAnchorId}`}>
                    {row.code}
                  </a>
                ) : (
                  <strong className="field-card-code">{row.code}</strong>
                )}
                <strong className="field-card-name">{row.name}</strong>
              </div>
              <div className="field-card-meta" aria-label={`${row.code} component details`}>
                {row.dataType && (
                  <span className="field-card-token">
                    <span>Type</span>
                    {row.dataTypePath ? (
                      <button className="table-link" type="button" onClick={() => onSelect(row.dataTypePath!)}>
                        {row.dataType}
                      </button>
                    ) : (
                      row.dataType
                    )}
                  </span>
                )}
                {row.table && (
                  <span className="field-card-token">
                    <span>Table</span>
                    {row.tablePath ? (
                      <button className="table-link" type="button" onClick={() => onSelect(row.tablePath!)}>
                        {row.table}
                      </button>
                    ) : (
                      row.table
                    )}
                  </span>
                )}
              </div>
              {row.description && <p className="component-card-description">{row.description}</p>}
            </article>
          );
        })}
      </div>
    </tablesection>
  );
}

function ValuesTable({ values }: { values: TableValue[] }) {
  if (!values.length) {
    return (
      <TableSection title="Values">
        <div className="empty-state table-empty-state">
          No common values are listed for this table. It may be obsolete, externally defined,
          locally defined, or not commonly enumerated. It is best avoided unless you have a
          specific profile, trading partner, or implementation use case.
        </div>
      </tablesection>
    );
  }

  return (
    <TableSection title="Values">
      <table>
        <thead>
          <tr>
            <th>Value</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {values.map((value) => (
            <tr key={value.value}>
              <td>
                <code>{value.value}</code>
              </td>
              <td>{value.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </tablesection>
  );
}

function TableSection({
  title,
  children,
  className = ""
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  const sectionClassName = ["table-section", className].filter(Boolean).join(" ");

  return (
    <section className={sectionClassName}>
      <h3>{title}</h3>
      <div className="table-scroll">{children}</div>
    </section>
  );
}

function NarrativeSections({ narratives }: { narratives: NarrativeBlock[] }) {
  if (!narratives.length) return null;
  return (
    <section className="narrative-section">
      <h3>Descriptions</h3>
      <div className="narrative-list">
        {narratives.map((narrative) => (
          <article className="narrative-block" key={`${narrative.source}-${narrative.title}`}>
            <div className="narrative-heading">
              <div>
                <h4>{narrative.title}</h4>
                <p>{narrative.source}</p>
              </div>
            </div>
            <p>{narrative.body}</p>
            {(narrative.citation || narrative.url) && (
              <div className="narrative-citation">
                {narrative.url ? (
                  <a href={narrative.url} target="_blank" rel="noreferrer">
                    <ExternalLink size={14} />
                    {narrative.citation ?? narrative.url}
                  </a>
                ) : (
                  <span>{narrative.citation}</span>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function Examples({ examples }: { examples: string[] }) {
  if (!examples.length) return null;
  return (
    <section className="examples-section">
      <h3>Example</h3>
      <pre>
        <code>{examples.join("\n")}</code>
      </pre>
    </section>
  );
}

function SourceResources({
  resources,
  title = "More Detail",
  compact = false,
  featured = false
}: {
  resources: SourceResource[];
  title?: string;
  compact?: boolean;
  featured?: boolean;
}) {
  if (!resources.length) return null;
  const sectionClassName = [
    "source-resources",
    compact ? "compact" : "",
    featured ? "featured" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={sectionClassName}>
      <h3>{title}</h3>
      <div className="resource-list">
        {resources.map((resource) => (
          <div className="resource-row" key={`${resource.source}-${resource.title}`}>
            <div>
              <strong>{resource.title}</strong>
              <small>
                {resource.source} · {resource.type}
              </small>
              {resource.description && <p>{resource.description}</p>}
            </div>
            {resource.url && (
              <a href={resource.url} target="_blank" rel="noreferrer" title="Open in a new tab">
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function ReferenceLinks({
  title,
  links,
  onSelect
}: {
  title: string;
  links: ReferenceLink[];
  onSelect: (path: string) => void;
}) {
  if (!links.length) return null;
  return (
    <section className="reference-links">
      <h3>{title}</h3>
      <div className="link-grid">
        {links.map((link) => {
          const Icon = kindIcons[link.kind];
          return (
            <button
              key={`${link.kind}-${link.code}-${link.path}`}
              className="related-link"
              type="button"
              onClick={() => onSelect(link.path)}
            >
              <Icon size={16} />
              <span>
                <strong>{link.code}</strong>
                <small>{link.title}</small>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SourceSummary({
  manifest,
  catalog,
  activeModuleName
}: {
  manifest: Manifest | null;
  catalog: Catalog | null;
  activeModuleName?: string;
}) {
  return (
    <div className="context-stack">
      <ProductPromotions />

      <section className="context-block notice-block">
        <p className="eyebrow">Legal</p>
        <p>{catalog?.legal.shortNotice}</p>
        {catalog?.legal.officialSourceUrl && (
          <a
            className="legal-source-link"
            href={catalog.legal.officialSourceUrl}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLink size={14} />
            HL7 official website
          </a>
        )}
        <p className="quiet">
          Official standards and license terms remain authoritative.
        </p>
      </section>

      <section className="context-block reference-version-block">
        <p className="eyebrow">Reference Version</p>
        <h2>{manifest?.label ?? "Loading"}</h2>
        <p>{manifest?.sourceSummary ?? "Loading reference details..."}</p>
        {manifest && (
          <dl className="compact-counts">
            {Object.entries(manifest.counts).map(([label, count]) => (
              <div key={label}>
                <dt>{formatCountLabel(label)}</dt>
                <dd>{count}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </div>
  );
}

function ProductPromotions() {
  return (
    <section className="product-promos" aria-label="Integration Soup products">
      <p className="eyebrow">Tools</p>
      <a
        className="product-card product-card-hl7"
        href="https://hl7soup.integrationsoup.com"
        target="_blank"
        rel="noreferrer"
      >
        <span className="product-card-head">
          <span className="product-icon" aria-hidden="true">
            <FileSearch size={20} />
          </span>
          <span className="product-copy">
            <strong>HL7 Soup Web</strong>
            <span>Web-based HL7 editor, testing, and message analysis.</span>
          </span>
        </span>
        <ul className="product-bullets">
          <li>Open, inspect, edit, and validate HL7 messages in the browser</li>
          <li>Conformance checks and message statistics</li>
          <li>Built-in anonymization for sensitive messages</li>
          <li>Send, receive, and test HL7 interfaces</li>
        </ul>
        <span className="product-cta">
          Open HL7 Soup Web
          <ExternalLink size={14} />
        </span>
        <img
          className="product-preview"
          src={withAppBasePath("/assets/hl7-soup-web-preview.webp")}
          alt="HL7 Soup Web message editor preview"
          loading="lazy"
        />
      </a>

      <a
        className="product-card product-card-integration"
        href="https://www.integrationsoup.com"
        target="_blank"
        rel="noreferrer"
      >
        <span className="product-card-head">
          <span className="product-icon" aria-hidden="true">
            <Activity size={20} />
          </span>
          <span className="product-copy">
            <strong>Integration Soup</strong>
            <span>Powerful healthcare integration engine for modern data flows.</span>
          </span>
        </span>
        <ul className="product-bullets">
          <li>Reduce integration costs without adding complexity</li>
          <li>Easy to build, monitor, and maintain</li>
          <li>Run on-premise, in the cloud, or hybrid</li>
          <li>Connect HL7 v2, FHIR, DICOM, REST/JSON services, and databases</li>
          <li>Transform CSV, XML, JSON, BIN/binary files, DICOM SR, and PDFs</li>
        </ul>
        <span className="product-cta">
          Explore Integration Soup
          <ExternalLink size={14} />
        </span>
        <img
          className="product-preview product-preview-integration"
          src={withAppBasePath("/assets/integration-soup-preview.webp")}
          alt="Integration Soup workflow designer preview"
          loading="lazy"
        />
      </a>
    </section>
  );
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <main className="error-screen">
      <InlineError message={message} />
    </main>
  );
}

function InlineError({ message }: { message: string }) {
  return (
    <div className="inline-error">
      <AlertCircle size={18} />
      <span>{message}</span>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="detail-skeleton">
      <div />
      <div />
      <div />
    </div>
  );
}

function getSampleMessageResources(detail: ReferenceDetail): SourceResource[] {
  if (detail.kind === "message") {
    const messageLabel = detail.messageType && detail.triggerEvent
      ? `${detail.messageType} ${detail.triggerEvent}`
      : detail.code;

    return [
      {
        title: `Sample ${detail.code} messages`,
        source: "HL7 Soup Web",
        type: "Example messages",
        description: `Open HL7 Soup Web to inspect and test sample HL7 messages while reading the ${messageLabel} structure.`,
        url: hl7SoupWebUrl
      }
    ];
  }

  if (detail.kind === "segment") {
    return [
      {
        title: `Sample messages using ${detail.code}`,
        source: "HL7 Soup Web",
        type: "Example messages",
        description: `Open HL7 Soup Web to inspect sample HL7 messages that include the ${detail.code} segment.`,
        url: hl7SoupWebUrl
      }
    ];
  }

  if (detail.kind === "field") {
    return [
      {
        title: `Sample messages for ${detail.segment}`,
        source: "HL7 Soup Web",
        type: "Example messages",
        description: `Open HL7 Soup Web to inspect sample messages that include ${detail.segment}, then compare how ${detail.code} is populated.`,
        url: hl7SoupWebUrl
      }
    ];
  }

  return [];
}

function singularKindLabel(kind: ReferenceKind) {
  switch (kind) {
    case "message":
      return "Message";
    case "segment":
      return "Segment";
    case "field":
      return "Field";
    case "datatype":
      return "Data Type";
    case "table":
      return "Table";
  }
}

function formatCountLabel(label: string) {
  return label.replace(/([A-Z])/g, " $1").replace(/^./, (value) => value.toUpperCase());
}

export default App;
